import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {Divider, Tooltip, useDisclosure} from "@chakra-ui/react";
import {FaRegCopy, FaRegEdit, FaRegTrashAlt, FaCog, FaRegQuestionCircle} from "react-icons/fa";
import {mondayOfWeek, parseAPIDate, toAPIDateString} from "../../../dateUtils";
import {CopyWeekDialog} from "./CopyWeekDialog";
import {EditWeekDialog} from "./EditWeekDialog";
import {ClearWeekDialog} from "./ClearWeekDialog";
import {HelpDialog} from "./HelpDialog";

interface WeekActionsToolbarProps {
    date: string;   // any date within the currently displayed period (TimeTable's `date` state)
    period: string; // "weekly" | "monthly"
}

/**
 * Copy Week / Edit / Clear / Help / Settings toolbar, ported from
 * script_plugin.user.js. Copy/Edit/Clear are only meaningful in Weekly view,
 * matching the script's isWeeklySelected() gate.
 *
 * Every control here is a plain button styled with the shared .tb-pill class
 * (see timetable.css) — the same class used by the Weekly/Monthly toggle and
 * the team/team-member tabs, so the whole toolbar is one visual language.
 */
export function WeekActionsToolbar({date, period}: WeekActionsToolbarProps) {
    const isWeekly = period === "weekly";
    const targetAnchor = toAPIDateString(mondayOfWeek(parseAPIDate(date)));
    const [openDialog, setOpenDialog] = useState<"copy" | "edit" | "clear" | null>(null);
    const helpDisclosure = useDisclosure();
    const navigate = useNavigate();

    const weeklyOnlyTitle = "Switch to Weekly view to use this action.";

    return (
        <div id="tb-week-actions-row" className="tb-pill-group">
            <Tooltip label={isWeekly ? "" : weeklyOnlyTitle} isDisabled={isWeekly}>
                <button className="tb-pill" disabled={!isWeekly} onClick={() => setOpenDialog("copy")}>
                    <FaRegCopy style={{marginRight: 6}}/> Copy Week
                </button>
            </Tooltip>
            <Tooltip label={isWeekly ? "" : weeklyOnlyTitle} isDisabled={isWeekly}>
                <button className="tb-pill" disabled={!isWeekly} onClick={() => setOpenDialog("edit")}>
                    <FaRegEdit style={{marginRight: 6}}/> Edit
                </button>
            </Tooltip>
            <Tooltip label={isWeekly ? "" : weeklyOnlyTitle} isDisabled={isWeekly}>
                <button className="tb-pill tb-pill--danger" disabled={!isWeekly} onClick={() => setOpenDialog("clear")}>
                    <FaRegTrashAlt style={{marginRight: 6}}/> Clear
                </button>
            </Tooltip>

            <Divider className="tb-hide-mobile" orientation="vertical" height="24px" borderColor="gray.300" _dark={{borderColor: "gray.600"}}/>

            <Tooltip label="Help">
                <button className="tb-pill tb-pill--icon tb-hide-mobile" aria-label="Help" onClick={helpDisclosure.onOpen}>
                    <FaRegQuestionCircle/>
                </button>
            </Tooltip>
            <Tooltip label="Favorites and hour step live on the Preferences page">
                <button className="tb-pill tb-pill--icon tb-hide-mobile" aria-label="Settings" onClick={() => navigate("/preferences")}>
                    <FaCog/>
                </button>
            </Tooltip>

            <CopyWeekDialog isOpen={openDialog === "copy"} onClose={() => setOpenDialog(null)} targetAnchor={targetAnchor}/>
            <EditWeekDialog isOpen={openDialog === "edit"} onClose={() => setOpenDialog(null)} targetAnchor={targetAnchor}/>
            <ClearWeekDialog isOpen={openDialog === "clear"} onClose={() => setOpenDialog(null)} targetAnchor={targetAnchor}/>
            <HelpDialog isOpen={helpDisclosure.isOpen} onClose={helpDisclosure.onClose}/>
        </div>
    );
}
